import { useAuthStore } from '../stores/authStore';
import { useCacheStore } from '../stores/cacheStore';
import { showNetworkError, showApiError } from '../stores/errorStore';

interface ApiRequestOptions extends RequestInit {
  params?: Record<string, string | number>;
}

interface ApiResponse<T = unknown> {
  success: boolean;
  data?: T;
  error?: {
    code: string;
    message: string;
  };
  pagination?: {
    page: number;
    pageSize: number;
    total: number;
    totalPages: number;
  };
}



class HttpClient {
  private baseURL: string;
  private defaultHeaders: Record<string, string>;

  constructor(baseURL: string = '/api') {
    this.baseURL = baseURL;
    this.defaultHeaders = {
      'Content-Type': 'application/json',
      'User-Agent': 'DriveCollectorAdmin/1.0',
      'Connection': 'keep-alive',
    };
  }

  private addAuthHeader(headers: HeadersInit): HeadersInit {
    const token = useAuthStore.getState().token;
    let newHeaders = { ...headers } as Record<string, string>;
    
    if (token) {
      newHeaders['Authorization'] = `Bearer ${token}`;
    }

    // Add Cache Provider header if selected
    // Note: We access the store state directly to avoid reactivity issues outside components
    try {
        const cacheProvider = useCacheStore.getState().selectedProvider;
        if (cacheProvider) {
          newHeaders['x-cache-provider'] = cacheProvider;
        }
    } catch (e) {
        // Ignore if store not ready
    }

    return newHeaders;
  }

  private buildUrl(endpoint: string, params?: Record<string, string | number>): string {
    const url = new URL(endpoint, this.baseURL);
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          url.searchParams.append(key, String(value));
        }
      });
    }
    return url.toString();
  }

  private async request<T>(
    endpoint: string,
    options: ApiRequestOptions = {}
  ): Promise<ApiResponse<T>> {
    const { params, headers = {}, ...rest } = options;

    const url = this.buildUrl(endpoint, params);
    const authHeaders = this.addAuthHeader(headers);

    try {
      const response = await fetch(url, {
        ...rest,
        signal: AbortSignal.timeout(30000), // 30秒超时
        headers: {
          ...this.defaultHeaders,
          ...authHeaders,
          ...headers,
        },
        // 添加一些兼容性选项
        keepalive: true,
      });

      const data = await response.json();

      // Handle API errors
      if (!data.success && data.error) {
        showApiError(data.error.message, data.error.code);
      }

      if (response.status === 401 && !endpoint.includes('/auth/login')) {
        useAuthStore.getState().logout();
        window.location.href = '/login';
        return data;
      }

      return data;
    } catch (error) {
      console.error('HTTP request error:', error);
      
      let errorMessage = 'Network error occurred';
      if (error instanceof Error) {
        console.error('Error name:', error.name);
        console.error('Error message:', error.message);
        if ((error as any).cause) {
          console.error('Error cause:', (error as any).cause);
        }
        errorMessage = `Network error: ${error.message}`;
        showNetworkError(error);
      }
      
      return {
        success: false,
        error: {
          code: 'NETWORK_ERROR',
          message: errorMessage,
        },
      };
    }
  }

  async get<T>(endpoint: string, options?: Omit<ApiRequestOptions, 'body'>): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { ...options, method: 'GET' });
  }

  async post<T>(endpoint: string, data?: Record<string, unknown>, options?: Omit<ApiRequestOptions, 'body'>): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async put<T>(endpoint: string, data?: Record<string, unknown>, options?: Omit<ApiRequestOptions, 'body'>): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async delete<T>(endpoint: string, data?: Record<string, unknown>, options?: Omit<ApiRequestOptions, 'body'>): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'DELETE',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async getBlob(endpoint: string, options?: Omit<ApiRequestOptions, 'body'>): Promise<Blob | null> {
    const { params, headers = {}, ...rest } = options || {};
    const url = this.buildUrl(endpoint, params);
    const authHeaders = this.addAuthHeader(headers);

    try {
      const response = await fetch(url, {
        ...rest,
        method: 'GET',
        headers: {
          ...this.defaultHeaders,
          ...authHeaders,
          ...headers,
        },
      });

      if (!response.ok) {
        console.error('Blob download failed:', response.status, response.statusText);
        return null;
      }

      return await response.blob();
    } catch (error) {
      console.error('Blob download error:', error);
      return null;
    }
  }
}

const apiURL = import.meta.env.VITE_API_URL;
export const httpClient = new HttpClient(apiURL);
